import React, { useState } from "react";
import { match, RouteComponentProps, withRouter } from "react-router-dom";
import clsx from "clsx";

import FormControl from "@material-ui/core/FormControl";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import SaveIcon from "@material-ui/icons/Save";
import AddIcon from "@material-ui/icons/Add";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import InputLabel from "@material-ui/core/InputLabel";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormHelperText from "@material-ui/core/FormHelperText";

import { racehorse360 } from "@tsg/1st-grpc-web-rh360";

import AppPageContent from "components/AppPageContent";
import Loader from "components/Loader";
import { useSnackbar } from "components/SnackbarContext/SnackbarContext";
import TrainerAutocomplete from "components/TrainerAutocomplete";
import NumberInput from "components/NumberInput";

import { SYSTEM } from "utils/token";
import {
  MAX_FIRST_NAME_LENGTH,
  MAX_LAST_NAME_LENGTH,
  MAX_LICENSE_NUMBER_LENGTH,
  phoneNumberMask
} from "common/constants";
import { highlightWithAsteriskIfError } from "utils/error-utils";
import UserRoleList from "./UserRoleList";
import { usePageState } from "./usePageState";
import useStyles from "./styles";

export type Props = RouteComponentProps<{ userId: string }>;

const EditUserPage = (props: Props) => {
  const { match } = props;
  const classes = useStyles();
  const [trainerSelectLabel, setTrainerSelectLabel] = useState(false);
  const {
    isLoading,
    userRoles,
    roles,
    facilities,
    editUser,
    isUserEditing,
    editUserForm,
    addUserRoleForm,
    assignRole,
    removeRole,
    requireFacility,
    requireTrainer
  } = usePageState(match.params.userId);

  const firstNameLabel = highlightWithAsteriskIfError(
    "First Name",
    Boolean(editUserForm.errors.firstName)
  );
  const lastNameLabel = highlightWithAsteriskIfError(
    "Last Name",
    Boolean(editUserForm.errors.lastName)
  );
  const emailLabel = highlightWithAsteriskIfError(
    "Email",
    Boolean(editUserForm.errors.email)
  );
  const licenseNumberLabel = highlightWithAsteriskIfError(
    "License Number",
    Boolean(editUserForm.errors.licenseNumber)
  );
  const phoneNumberLabel = highlightWithAsteriskIfError(
    "Phone Number",
    Boolean(editUserForm.errors.phoneNumber)
  );

  const checkErrorInField = (field: string): string => {
    return editUserForm.touched[field] && editUserForm.errors[field];
  };

  const hasFirstNameError = checkErrorInField("firstName");
  const hasLastNameError = checkErrorInField("lastName");
  const hasEmailError = checkErrorInField("email");
  const hasLicenseNumberError = checkErrorInField("licenseNumber");
  const hasPhoneNumberError = checkErrorInField("phoneNumber");

  const { showErrorSnack } = useSnackbar();

  const RolesDisplay = (
    isLoading: boolean,
    facilities: racehorse360.IFacility[],
    userRoles: racehorse360.IUserRole[],
    roles: racehorse360.IRole[],
    assignRole: (
      newRole: racehorse360.IAssignRoleRequest
    ) => Promise<racehorse360.IUser>,
    removeRole: (
      newRole: racehorse360.IRemoveRoleRequest
    ) => Promise<racehorse360.IUser>,
    requireFacility: (role: racehorse360.IRole) => boolean,
    requireTrainer: (role: racehorse360.IRole) => boolean,
    addUserRoleForm,
    match: match<{
      userId: string;
    }>
  ) => {
    const handleRoleChange = async (
      event: React.ChangeEvent<{ value: string }>
    ) => {
      const selectedRoleId = event.target.value;
      const role = roles.find(r => r.id === selectedRoleId);
      await addUserRoleForm.setFieldValue("role", role, true);

      if (role.name === "Assistant Trainer") {
        setTrainerSelectLabel(true);
      } else if (role.name === "Trainer") {
        setTrainerSelectLabel(false);
      }

      if (!requireTrainer(addUserRoleForm.values.role)) {
        await addUserRoleForm.setFieldValue("trainer", null, true);
      }

      if (!requireFacility(addUserRoleForm.values.role)) {
        await addUserRoleForm.setFieldValue("facility", null, true);
      }
    };

    const handleFacilityChange = async (
      event: React.ChangeEvent<{ value: string }>
    ) => {
      const selectedFacilityId = event.target.value;
      const facility = facilities.find(f => f.id === selectedFacilityId);
      await addUserRoleForm.setFieldValue("facility", facility, true);
    };

    const canAddRole =
      addUserRoleForm.isValid &&
      (!userRoles?.some(userRole => userRole.role.id !== SYSTEM.DEVELOPER) ||
        userRoles?.some(
          userRole => userRole.role.id === addUserRoleForm.values.role?.id
        ) ||
        addUserRoleForm.values.role?.id === SYSTEM.DEVELOPER);

    return (
      <Paper
        elevation={3}
        className={clsx(classes.pagePaper, classes.pagePaperFlexColumn)}
      >
        <div className={classes.paperFlexTop}>
          <FormControl
            error={
              addUserRoleForm.touched.role && !!addUserRoleForm.errors.role
            }
          >
            <InputLabel id="roleLabel">Requested User Role</InputLabel>
            <Select
              style={{ width: "200px" }}
              disabled={isLoading || addUserRoleForm.isSubmitting}
              name="role"
              displayEmpty
              labelId="roleLabel"
              fullWidth={true}
              value={
                addUserRoleForm.values["role"]
                  ? addUserRoleForm.values["role"].id
                  : ""
              }
              renderValue={() => (
                <>
                  {addUserRoleForm.values["role"]
                    ? addUserRoleForm.values["role"].name
                    : ""}
                </>
              )}
              onChange={handleRoleChange}
              onBlur={addUserRoleForm.handleBlur}
              error={
                addUserRoleForm.touched.role &&
                Boolean(addUserRoleForm.errors.role)
              }
            >
              {roles.map(role => (
                <MenuItem key={role.id} value={role.id}>
                  <div>{role.name}</div>
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {addUserRoleForm.touched.role && addUserRoleForm.errors.role}
            </FormHelperText>
          </FormControl>
          {requireFacility(addUserRoleForm.values.role) && (
            <FormControl
              error={
                addUserRoleForm.touched.facility &&
                !!addUserRoleForm.errors.facility
              }
            >
              <InputLabel id="facilityLabel">At Facility</InputLabel>
              <Select
                style={{ width: "200px" }}
                disabled={isLoading}
                name="facility"
                displayEmpty
                labelId="facilityLabel"
                value={
                  addUserRoleForm.values["facility"]
                    ? addUserRoleForm.values["facility"].id
                    : ""
                }
                renderValue={() => (
                  <>
                    {addUserRoleForm.values["facility"]
                      ? addUserRoleForm.values["facility"].name
                      : ""}
                  </>
                )}
                onChange={handleFacilityChange}
                onBlur={addUserRoleForm.handleBlur}
                error={
                  addUserRoleForm.touched.facility &&
                  Boolean(addUserRoleForm.errors.facility)
                }
              >
                {facilities.map(facility => (
                  <MenuItem key={facility.id} value={facility.id}>
                    <div>{facility.name}</div>
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {addUserRoleForm.touched.facility &&
                  addUserRoleForm.errors.facility}
              </FormHelperText>
            </FormControl>
          )}

          {requireTrainer(addUserRoleForm.values.role) && (
            <FormControl
              error={
                addUserRoleForm.touched.trainer &&
                !!addUserRoleForm.errors.trainer
              }
            >
              <TrainerAutocomplete
                onChange={trainer =>
                  addUserRoleForm.setFieldValue("trainer", trainer)
                }
                trainerAssistant={trainerSelectLabel}
              />
              <FormHelperText>
                {addUserRoleForm.touched.trainer &&
                  addUserRoleForm.errors.trainer}
              </FormHelperText>
            </FormControl>
          )}

          <Button
            type="submit"
            variant="contained"
            disabled={!canAddRole}
            onClick={() =>
              assignRole({
                id: match.params.userId,
                roleId: addUserRoleForm.values.role.id,
                facilityId: addUserRoleForm.values.facility?.id,
                trainerId: addUserRoleForm.values.trainer?.id
              })
            }
            className={classes.submitBtn}
          >
            <AddIcon />
            Add Role
          </Button>
        </div>
        <div className={classes.paperFlexBottom}>
          {!isLoading && (
            <UserRoleList
              userRoles={userRoles}
              onRemove={role =>
                removeRole({
                  id: match.params.userId,
                  roleId: role.role.id,
                  facilityId: role.facility?.id,
                  trainerId: role.trainer?.id
                })
              }
            />
          )}
        </div>
      </Paper>
    );
  };

  const rolesDisplay = RolesDisplay(
    isLoading,
    facilities,
    userRoles,
    roles,
    assignRole,
    removeRole,
    requireFacility,
    requireTrainer,
    addUserRoleForm,
    match
  );

  const handleFormSubmit = () => {
    editUser({
      id: match.params.userId,
      firstName: editUserForm.values.firstName,
      lastName: editUserForm.values.lastName,
      email: editUserForm.values.email,
      phoneNumber: editUserForm.values.phoneNumber,
      licenseNumber: editUserForm.values.licenseNumber
    }).catch((error: Error) => {
      showErrorSnack(error.message);
    });
  };

  return (
    <AppPageContent className={classes.appPageContent}>
      <Typography variant="subtitle1" align="left" className={classes.subtitle}>
        Edit User
      </Typography>
      <Paper
        elevation={3}
        className={clsx(classes.pagePaper, classes.paperTextFields)}
      >
        <TextField
          label={firstNameLabel}
          disabled={isLoading}
          id="firstName"
          name="firstName"
          autoComplete="off"
          onBlur={editUserForm.handleBlur}
          value={editUserForm.values.firstName}
          onChange={editUserForm.handleChange}
          inputProps={{ maxLength: MAX_FIRST_NAME_LENGTH }}
          error={Boolean(hasFirstNameError)}
          helperText={hasFirstNameError || ""}
          FormHelperTextProps={{ classes: { root: classes.rootHelperText } }}
        />

        <TextField
          label={lastNameLabel}
          disabled={isLoading}
          id="lastName"
          name="lastName"
          autoComplete="off"
          onBlur={editUserForm.handleBlur}
          value={editUserForm.values.lastName}
          onChange={editUserForm.handleChange}
          inputProps={{ maxLength: MAX_LAST_NAME_LENGTH }}
          error={Boolean(hasLastNameError)}
          helperText={hasLastNameError || ""}
          FormHelperTextProps={{ classes: { root: classes.rootHelperText } }}
        />

        <TextField
          label={emailLabel}
          disabled={isLoading}
          id="email"
          name="email"
          autoComplete="off"
          onBlur={editUserForm.handleBlur}
          value={editUserForm.values.email}
          onChange={editUserForm.handleChange}
          error={Boolean(hasEmailError)}
          helperText={hasEmailError || ""}
          FormHelperTextProps={{ classes: { root: classes.rootHelperText } }}
        />

        <TextField
          label={licenseNumberLabel}
          disabled={isLoading}
          id="licenseNumber"
          name="licenseNumber"
          autoComplete="off"
          onBlur={editUserForm.handleBlur}
          value={editUserForm.values.licenseNumber}
          onChange={editUserForm.handleChange}
          inputProps={{ maxLength: MAX_LICENSE_NUMBER_LENGTH }}
          error={Boolean(hasLicenseNumberError)}
          helperText={hasLicenseNumberError || ""}
          FormHelperTextProps={{ classes: { root: classes.rootHelperText } }}
        />

        <NumberInput
          mask={phoneNumberMask}
          label={phoneNumberLabel}
          variant="standard"
          outlined
          autoComplete="off"
          id="phoneNumber"
          name="phoneNumber"
          value={editUserForm.values.phoneNumber}
          onTextChange={editUserForm.handleChange("phoneNumber")}
          onChange={editUserForm.handleChange}
          onBlur={editUserForm.handleBlur}
          error={Boolean(hasPhoneNumberError)}
          helperText={hasPhoneNumberError || ""}
          FormHelperTextProps={{ classes: { root: classes.rootHelperText } }}
        />

        <Button
          type="submit"
          disabled={!editUserForm.isValid || isUserEditing}
          variant="contained"
          className={classes.saveBtn}
          onClick={handleFormSubmit}
        >
          <SaveIcon />
          Save
        </Button>
      </Paper>
      <Typography variant="subtitle1" align="left" className={classes.subtitle}>
        Roles
      </Typography>
      {rolesDisplay}
      {(isUserEditing || isLoading) && <Loader overlay />}
    </AppPageContent>
  );
};

export default withRouter(EditUserPage);
